/**
 * TransactionSlideOver — read-only preview panel for a single
 * transaction. Mirrors the mobile detail screen at
 *   /Users/gudupuramesh/Documents/siteexpens/app/(app)/projects/[id]/transaction/[txnId].tsx
 *
 * Status: PREVIEW ONLY in this turn.
 *   - Hero amount + workflow ribbon + status pill
 *   - Details (party, date, description, reference)
 *   - Category + method
 *   - Receipt photo (first one only; multi-photo gallery deferred)
 *
 * Deferred to next turn:
 *   - Edit button → Edit form
 *   - Approve / Reject (admin-only)
 *   - Settlement card (mark cleared / proof of payment)
 *   - Delete
 *   - PDF receipt generation
 */
import { useEffect } from 'react';

import {
  categoryLabel,
  paymentMethodLabel,
  type PaymentMethod,
  type TransactionCategory,
} from '@/lib/transactions';
import {
  normaliseTxType,
  txDateMs,
  useTransaction,
  type TransactionItem,
} from '@/lib/useProjectTransactions';

export type TransactionSlideOverProps = {
  txnId: string | null;
  onClose: () => void;
};

// ───── Helpers ─────

function formatInr(n: number): string {
  return n.toLocaleString('en-IN');
}

function formatDateLong(ms: number | null): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

function workflowRibbonContent(t: TransactionItem): {
  label: string;
  bg: string;
  fg: string;
  body: string;
} | null {
  if (t.workflowStatus === 'pending_approval') {
    return {
      label: 'PENDING APPROVAL',
      bg: '#FEF3C7',
      fg: '#92400E',
      body: 'This entry is not in your ledger totals yet. An admin must approve it first.',
    };
  }
  if (t.workflowStatus === 'rejected') {
    return {
      label: 'REJECTED',
      bg: '#FECACA',
      fg: '#991B1B',
      body: t.rejectionNote?.trim() || 'An admin rejected this entry.',
    };
  }
  return null;
}

function statusPill(t: TransactionItem): { label: string; bg: string; fg: string } {
  switch (t.status) {
    case 'pending': return { label: 'Pending', bg: '#FEF3C7', fg: '#D97706' };
    case 'partial': return { label: 'Partial', bg: '#FFEDD5', fg: '#C2410C' };
    case 'paid':    return { label: 'Paid',    bg: '#D1FAE5', fg: '#15803D' };
    default:        return { label: 'Paid',    bg: '#D1FAE5', fg: '#15803D' };
  }
}

// ───── Component ─────

export function TransactionSlideOver({ txnId, onClose }: TransactionSlideOverProps) {
  const open = txnId !== null;
  const { transaction: t, loading, error } = useTransaction(txnId ?? undefined);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  return (
    <>
      <div
        className={`slide-overlay${open ? ' open' : ''}`}
        onClick={onClose}
        aria-hidden
      />
      <div
        className={`slide-over${open ? ' open' : ''}`}
        role="dialog"
        aria-label="Transaction details"
        aria-hidden={!open}
      >
        <div className="slide-header">
          <h2 className="slide-title">Transaction</h2>
          <button
            type="button"
            className="slide-close"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="ph ph-x" />
          </button>
        </div>

        <div className="slide-body">
          {!open ? null : loading ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-secondary)' }}>
              Loading…
            </div>
          ) : error ? (
            <div
              className="card"
              style={{
                padding: 12,
                background: 'var(--critical-bg)',
                color: 'var(--critical-text)',
                borderColor: 'var(--critical)',
              }}
            >
              <i className="ph ph-warning-circle" /> {error}
            </div>
          ) : !t ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-secondary)' }}>
              Transaction not found.
            </div>
          ) : (
            <>
              {/* Hero — big signed amount + status pill */}
              {(() => {
                const kind = normaliseTxType(t.type);
                const sign = kind === 'out' ? '−' : '+';
                const color = kind === 'out' ? 'var(--critical)' : 'var(--success)';
                const pill = statusPill(t);
                return (
                  <div
                    className="card"
                    style={{
                      padding: 20,
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 12,
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: 'var(--text-tertiary)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          marginBottom: 4,
                        }}
                      >
                        {kind === 'out' ? 'Payment Out' : 'Payment In'}
                      </div>
                      <div
                        style={{
                          fontSize: 28,
                          fontWeight: 700,
                          color,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {sign}₹{formatInr(t.amount ?? 0)}
                      </div>
                    </div>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        padding: '4px 8px',
                        borderRadius: 999,
                        background: pill.bg,
                        color: pill.fg,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                      }}
                    >
                      {pill.label}
                    </span>
                  </div>
                );
              })()}

              {/* Workflow ribbon */}
              {(() => {
                const r = workflowRibbonContent(t);
                if (!r) return null;
                return (
                  <div
                    style={{
                      padding: 12,
                      borderRadius: 8,
                      background: r.bg,
                      color: r.fg,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                    }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em' }}>
                      {r.label}
                    </div>
                    <div style={{ fontSize: 13, lineHeight: 1.5 }}>{r.body}</div>
                  </div>
                );
              })()}

              {/* Details card */}
              <div className="card" style={{ padding: 16 }}>
                <div className="detail-row">
                  <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                    <i className="ph ph-user" /> Party
                  </span>
                  <span style={{ fontWeight: 500 }}>{t.partyName?.trim() || '—'}</span>
                </div>
                <div className="detail-row">
                  <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                    <i className="ph ph-calendar" /> Date
                  </span>
                  <span style={{ fontWeight: 500 }}>{formatDateLong(txDateMs(t))}</span>
                </div>
                <div className="detail-row">
                  <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                    <i className="ph ph-tag" /> Category
                  </span>
                  <span style={{ fontWeight: 500 }}>
                    {categoryLabel(t.category as TransactionCategory)}
                  </span>
                </div>
                <div className="detail-row">
                  <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                    <i className="ph ph-credit-card" /> Method
                  </span>
                  <span style={{ fontWeight: 500 }}>
                    {paymentMethodLabel(t.paymentMethod as PaymentMethod)}
                  </span>
                </div>
                {t.referenceNumber?.trim() ? (
                  <div className="detail-row">
                    <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                      <i className="ph ph-hash" /> Reference
                    </span>
                    <span style={{ fontWeight: 500, fontFamily: 'monospace' }}>
                      {t.referenceNumber}
                    </span>
                  </div>
                ) : null}
              </div>

              {/* Description */}
              {t.description?.trim() ? (
                <div className="detail-section">
                  <div className="detail-label">Description</div>
                  <p
                    style={{
                      fontSize: 13,
                      color: 'var(--text-primary)',
                      background: '#FAFAFA',
                      padding: 12,
                      borderRadius: 6,
                      border: '1px solid var(--border)',
                      lineHeight: 1.5,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {t.description}
                  </p>
                </div>
              ) : null}

              {/* Receipt photo (first one only for now) */}
              {(() => {
                const photo = t.photoUrls?.[0] ?? t.photoUrl;
                if (!photo) return null;
                return (
                  <div className="detail-section">
                    <div className="detail-label">Receipt</div>
                    <a
                      href={photo}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'block',
                        borderRadius: 8,
                        overflow: 'hidden',
                        border: '1px solid var(--border)',
                      }}
                    >
                      <img
                        src={photo}
                        alt="Receipt"
                        style={{ width: '100%', display: 'block' }}
                      />
                    </a>
                  </div>
                );
              })()}

              {/* Footer placeholder — Edit / Approve / Reject coming
                  in the next turn. */}
              <div
                style={{
                  marginTop: 16,
                  padding: 12,
                  borderRadius: 8,
                  background: '#FAFAFA',
                  fontSize: 12,
                  color: 'var(--text-tertiary)',
                  textAlign: 'center',
                }}
              >
                Edit · Approve · Reject · Delete · PDF receipt — ships next.
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
